// Org entity hooks — the WRAPPER layer (lint-exempt for raw TanStack Query).
// An organization is an ENTITY realm (not band-tenanted): the keys live under ['org', orgId, …] so they
// survive a band switch (which scorches ['band', …]). An org is created only by an /apply approval, never
// here. Every write surfaces the server error verbatim and NEVER reports success on an
// unpersisted write — the modal/form catches it and shows it inline.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";
import { API_URL } from "../../config/env.js";

type OrgView = components["schemas"]["OrgView"];
type UpdateOrgRequest = components["schemas"]["UpdateOrgRequest"];

export type { OrgView, UpdateOrgRequest };

/**
 * The org-type taxonomy — a fixed UI vocabulary mirroring `core.org_role_catalog.org_type`
 * (label|management|booking). `org_type` is org identity and IMMUTABLE
 * post-creation, so it is a create-time picker only. This is the presentation label, never a
 * business value (is prices/tiers/quotas/windows).
 */
export const ORG_TYPES = ["label", "management", "booking"] as const;
export type OrgType = (typeof ORG_TYPES)[number];

/** Title-case display label for an org/link type token (falls back to the raw string). */
export function orgTypeLabel(type: string | null | undefined): string {
  switch (type) {
    case "label":
      return "Label";
    case "management":
      return "Management";
    case "booking":
      return "Booking";
    default:
      return type ? type.charAt(0).toUpperCase() + type.slice(1) : "Organization";
  }
}

/** Two initials for the logo fallback avatar (born-blank → "?"). */
export function orgInitials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const s = `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
  return s || "?";
}

/** `GET /orgs/{orgId}` — the org entity (name/type/logo/contact). A 404 surfaces as the query error state. */
export function useOrg(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
  });
}

/** `PUT /orgs/{orgId}` (edit; `orgType` is immutable, never sent) → OrgView. Reseeds the org cache.*/
export function useUpdateOrg(orgId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOrgRequest) => {
      const { data, error, response } = await api.PUT("/api/v1/orgs/{orgId}", {
        params: { path: { orgId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["org", orgId], data);
    },
  });
}

/**
 * `PUT /orgs/{orgId}/logo` (multipart; jpeg/png/webp ≤ 5 MB) → OrgView (ART-337). The org's square logo: it
 * replaces the All-roster tile in the roster rail and shows wherever the org's picture does. Uploads
 * immediately (the AvatarEditor rule) rather than riding the profile form's Save. Raw fetch, like every
 * multipart here; reseeds the org cache with the answer so every surface repaints off the new
 * `logoMediaId` (the cache-bust key).
 */
export function useSetOrgLogo(orgId: string) {
  const { tokenProvider } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append("file", file);
      const bearer = tokenProvider.getBearer();
      const res = await fetch(`${API_URL}/api/v1/orgs/${orgId}/logo`, {
        method: "PUT",
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
        credentials: "include",
        body,
      });
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      return (await res.json()) as OrgView;
    },
    onSuccess: (data) => qc.setQueryData(["org", orgId], data),
  });
}

/** `DELETE /orgs/{orgId}/logo` → OrgView. The roster falls back to its default tile. */
export function useClearOrgLogo(orgId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.DELETE("/api/v1/orgs/{orgId}/logo", {
        params: { path: { orgId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (data) => qc.setQueryData(["org", orgId], data),
  });
}
